import { getHourDifference, longDateFormat } from "@components/inputs/date/utils";
import { getHoursUnit, IFieldInfoProperties } from "@components/smart/FieldInfo";
import { addEmptyLineItem } from "@components/smart/smartFastEntryList";
import { getExpandedEnumProps } from "@odata/Data.utils";
import {
    EntityTypeName,
    IPrWorkingPatternDayEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrWorkingPatternDayEntity,
    PrWorkingPatternDayIntervalEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternIntervalTypeCode, PrWorkingPatternTypeCode } from "@odata/GeneratedEnums";
import { getHoursFromTimeStamp } from "@pages/payroll/attendance/Attendance.utils";
import { normalizeIntervals } from "@pages/payroll/workingPatterns/WorkDayIntervals.utils";
import { rotationLengthPath, timeDiffPath } from "@pages/payroll/workingPatterns/WorkingPatternsDef";
import { isDefined, isNotDefined, roundToDecimalPlaces } from "@utils/general";
import Big from "big.js";

import { BREAK_LENGTH, DASH_CHARACTER, WORK_LENGTH } from "../../../constants";
import { FieldType, ValidatorType } from "../../../enums";
import DateType, { getDateFormat, getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";

export const WORKING_HOURS_DECIMAL_PLACES = 3;
export const DEFAULT_FULL_TIME_HOURS_PER_WEEK = 40;


export const getHoursDiffFormatted = (start: Date, end: Date, overMidnight: boolean): string => {
    const diff = getHourDifference(getUtcDayjs(start), getUtcDayjs(end), overMidnight);
    if (isNotDefined(start) || isNotDefined(end) || isNaN(diff) || diff < 0) {
        return DASH_CHARACTER;
    }
    const dateDiff = getUtcDayjs().startOf("day").add(diff, "hours");
    return DateType.format(dateDiff, longDateFormat(getDateFormat(DateType.defaultTimeFormat)));
};

export function getWeeklyWorkingHours(entity: IPrWorkingPatternEntity, _rotationLength?: number): number {
    const typeCode = entity?.TypeCode;
    const hasDifferentOddAndEvenWeek =
        entity?.IsDifferentOddAndEvenWeek &&
        typeCode === PrWorkingPatternTypeCode.Weekly;
    const days = entity?.Days ?? [];

    const rotationLength = _rotationLength ?? days.length;
    if (typeCode === PrWorkingPatternTypeCode.Uneven) {
        return roundToDecimalPlaces(WORKING_HOURS_DECIMAL_PLACES, (entity?.WorkingMinutesWeekly ?? 0) / 60);
    }

    let sum = new Big(0);
    for (const day of days) {
        if (day.WorkingHours) {
            sum = sum.plus(getHoursFromTimeStamp(day.WorkingHours));
        }
    }

    const isRotationWorkingPatternTypeCode =
        typeCode === PrWorkingPatternTypeCode?.Rotation &&
        isDefined(rotationLength) &&
        Number.isInteger(rotationLength) &&
        rotationLength > 0;
    let numberOfWeeks = isRotationWorkingPatternTypeCode
        ? new Big(rotationLength).div(7)
        : new Big(hasDifferentOddAndEvenWeek ? 2 : 1);

    // to avoid division by zero, although it should not happen due to validation
    numberOfWeeks = numberOfWeeks.gt(0) ? numberOfWeeks : new Big(1);

    return roundToDecimalPlaces(WORKING_HOURS_DECIMAL_PLACES, sum.div(numberOfWeeks).toNumber());
}

export const BaseWorkingHoursPerWeekDef: IFieldInfoProperties = {
    type: FieldType.NumberInput,
    fieldSettings: {
        unit: getHoursUnit,
        max: 40,
        min: 0,
    },
    validator: {
        type: ValidatorType.PositiveNumber,
        settings: {
            min: 0,
            max: 40,
        },
    },
    useForValidation: true,
};

export function recalculateWeeklyWorkingHours(storage: FormStorage): void {
    const entity = storage.getEntity<IPrWorkingPatternEntity>();
    const workingMinutesWeekly = getWeeklyWorkingHours(entity, storage.getValueByPath(rotationLengthPath)) * 60;
    storage.setValueByPath(PrWorkingPatternEntity.WorkingMinutesWeekly, Math.ceil(workingMinutesWeekly) || undefined);
}

export const deleteIntervalErrors = (storage:FormStorage, day: IPrWorkingPatternDayEntity) => {
    const intervalsBc = storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days).addKey(day).navigate(PrWorkingPatternDayEntity.Intervals);
    const path = intervalsBc.getFullPath(false);
    storage.clearCollectionAdditionalFieldDataByPath(path);
};

export const getNewEmptyIntervalLineItem = (storage: FormStorage, newItemsMax?: number): Partial<IPrWorkingPatternDayIntervalEntity> => {
    return addEmptyLineItem<IPrWorkingPatternDayIntervalEntity>({
        storage,
        bindingContext: storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days).navigate(PrWorkingPatternDayEntity.Intervals),
        newItemsCount: (newItemsMax ?? 0) + 1,
        columns: [{
            id: PrWorkingPatternDayIntervalEntity.TimeStart
        }, {
            id: PrWorkingPatternDayIntervalEntity.TimeEnd
        }, {
            id: timeDiffPath
        }],
        context: storage.context
    });
};

export const createDefaultIntervalsForDay = (storage: FormStorage, workingHours: number): IPrWorkingPatternDayIntervalEntity[] => {
    const intervals: IPrWorkingPatternDayIntervalEntity[] = [];

    let hoursLeft = workingHours;
    let lastWasBreak = true;
    let startDate = getUtcDayjs(storage.data.entity.DefaultDayStart) ?? getUtcDayjs().startOf("day").add(8, "hours");

    while (hoursLeft > 0) {
        const endDate = startDate.clone().add(lastWasBreak ? Math.min(hoursLeft, WORK_LENGTH) : BREAK_LENGTH, "hours");
        let newInterval = getNewEmptyIntervalLineItem(storage, intervals.length);
        const props = getExpandedEnumProps(EntityTypeName.PrWorkingPatternIntervalType, lastWasBreak ? PrWorkingPatternIntervalTypeCode.Work : PrWorkingPatternIntervalTypeCode.MealBreak, PrWorkingPatternDayIntervalEntity.Type);
        newInterval = {
            ...newInterval,
            TimeStart: startDate.clone().toDate(),
            TimeEnd: endDate.toDate(),
            ...props
        };
        startDate = endDate.clone();
        intervals.push(newInterval);
        if (lastWasBreak) {
            hoursLeft -= WORK_LENGTH;
        }
        lastWasBreak = !lastWasBreak;
    }
    normalizeIntervals(intervals, 0);
    return intervals;
};